import { NestedTreeControl } from '@angular/cdk/tree';
import { CommonModule } from '@angular/common';
import { Component, inject, signal, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatTreeModule, MatTreeNestedDataSource } from '@angular/material/tree';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { Loader } from '../../loader/loader';
import { ReportV2Details, ReportV2Service } from '../../services/report-v2.service';

import { SnackbarService } from '../../services/snackbar.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { MatDialogModule } from '@angular/material/dialog';
import { DialogueBox } from '../../dialogue-box/dialogue-box';

import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../auth/auth.service';

import { AngularTiptapEditorComponent } from '@flogeez/angular-tiptap-editor';
import { ReportOutline } from '../../components/report-outline/report-outline';
import { ReportScores } from '../../components/report-scores/report-scores';
import { StorageService } from '../../auth/storage.service';
import { DialogueTexts } from '../../constants/dialog-texts.constant';
import { ValidationMessagesTexts } from '../../constants/validation-messages.constants';
import { ReportStatus } from '../../models/report';
import { ApiResponseMessages } from '../../constants/api-response-messages.constants';
import { UserRolePermissionResponse } from '../../models/user-roles-permissions';
import { SearchableDropdown } from '../../components/searchable-dropdown/searchable-dropdown';
import { Labels } from '../../constants/labels-constant';
import { BlockSpecialCharsAndAlphabatesDirective } from '../../directives/block-special-chars';
import { SectionV2 } from '../../models/v2models/report-sections-v2';
import { ReviewerV2 } from '../../models/v2models/reviewer-v2';
import { SectionStatus } from '../../models/v2models/section-status-v2';

interface Section {
  id: number;
  sectionNumber: string;
  name: string;
  content: string;
  isEditing: boolean;
  isNew?: boolean;
  originalState?: { name: string; content: string };
  revisionRequested?: number;
  parentSectionId?: number | null;
  showBotProgress: boolean;
  reviewerIds: string[] | null;
  isEditable: boolean;
  sectionScore: number | null;
  status?: string;
}
interface TocNode {
  id: string;
  name: string;
  children?: TocNode[];
}

@Component({
  selector: 'app-report-sections',
  standalone: true,
  imports: [
    CommonModule,
    MatTreeModule,
    MatIconModule,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatDialogModule,
    FormsModule,
    Loader,
    DialogueBox,
    AngularTiptapEditorComponent,
    ReportOutline,
    ReportScores,
    ReactiveFormsModule,
    SearchableDropdown,
    BlockSpecialCharsAndAlphabatesDirective,
  ],
  templateUrl: './report-sections.html',
  styleUrl: './report-sections.scss',
  providers: [],
})
export class ReportSections implements OnInit {
  private storageService = inject(StorageService);
  private reportV2Service = inject(ReportV2Service);
  private snackbar = inject(SnackbarService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private authService = inject(AuthService);

  roles: string[] = [];
  referenceFiles: string[] = [];
  responseData: any;
  initialGenerate: boolean = false;
  treeControl = new NestedTreeControl<TocNode>((node) => node.children);
  showScoreBoard = false;
  notesStatus = false;
  notesClosing = false;
  isTreeCollapsed = false;
  dataSource = new MatTreeNestedDataSource<TocNode>();
  isLoading = false;
  outlineAgentData: any = {};
  scoreData: any = {};
  qualityScore: any = [];
  overAllQualityScore: string | null = null;
  isWriter: boolean = false;
  isReviewer: boolean = false;
  apiResponse = {};
  sectionsData: Section[] = [];
  status!: number;
  selectedSectionNumber: string | '' = '';
  clientName: string = '';
  locationName: string | null = '';
  createdBy: string = '';
  statusLabel: string = '';
  overallQuality: number | null = null;
  reportId: number = 0;
  isDialogOpen = false;
  isReferenceDialogOpen = false;
  isApprovalDialogOpen = false;
  isClosing = false;
  isReferenceClosing = false;
  isApprovalDialogClosing = false;
  isDeleteDialogOpen = false;
  isReportStatusDialogOpen = false;
  sectionNumberList: any = [];
  reviewerList: ReviewerV2[] = [];
  deleteCommentRequest: { sectionId: number; commentId: string } | null = null;
  selectedSectionId: number | null = null;
  addAsChild: boolean = false;
  newSectionContent: string = '';
  newSectionTitle: string = '';
  newSectionNumber: string | null = null;
  selectedSectionIdForComments: number | 0 = 0;
  initials: string = '';
  name: string = '';
  reportStatusDialogButtonText = '';
  reportStatusDialogMessage = '';
  checkedSections = new Set<number>();
  reportStatus: number | null = null;
  currentUserId: string | null = null;
  currentUserEmail: string | null = null;
  selectedSection: Section | null = null;

  dialogTexts = DialogueTexts;
  labels = Labels;
  validationMessages = ValidationMessagesTexts;
  apiResponseMessages = ApiResponseMessages;
  reportStatusCodes = ReportStatus;
  reworkSectionIds: number[] = [];
  private reworkInterval: any;
  isDeleteCommentDialogOpen = false;
  isApproveSectionDialogOpen = false;
  isApproveSectionDialogClosing = false;
  approveFeedback: string = '';
  feedbackDialogAction: 'approve' | 'reject' = 'approve';
  IsPublishEnabled: boolean = false;

  commentsCount: number = 0;

  openDialog() {
    this.isDialogOpen = true;
    this.isClosing = false;
  }

  closeDialog() {
    this.isClosing = true;

    setTimeout(() => {
      this.isDialogOpen = false;
      this.isClosing = false;
      this.addAsChild = false;
      this.newSectionContent = '';
      this.newSectionTitle = '';
      this.newSectionNumber = null;
      this.selectedSectionId = null;
    }, 300);
  }

  closeDeleteDialog() {
    this.isDeleteDialogOpen = false;
  }

  closeReportStatusDialog() {
    this.isReportStatusDialogOpen = false;
    this.router.navigate(['/reports']);
  }

  openDeleteDialogForComment(sectionId: number, commentId: string) {
    this.isDeleteCommentDialogOpen = true;
    this.deleteCommentRequest = { sectionId, commentId };
  }

  deleteSectionComment() {
    if (!this.deleteCommentRequest) {
      this.isLoading = false;
      return;
    }
    this.isLoading = true;
    this.isDeleteCommentDialogOpen = false;
    this.reportV2Service
      .deleteComment(
        this.reportId,
        this.deleteCommentRequest.sectionId,
        this.deleteCommentRequest.commentId,
      )
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          if (response.success) {
            this.notes = this.notes.filter(
              (n) => n.commentId !== this.deleteCommentRequest!.commentId,
            );
            this.commentsCount--;
            this.snackbar.showCustom({
              type: 'success',
              title: this.validationMessages.TOASTER.SUCCESS.TITLE,
              message: this.apiResponseMessages.COMMENT_DELETED,
            });
          } else {
            this.snackbar.showCustom({
              type: 'error',
              title: this.validationMessages.TOASTER.ERROR.TITLE,
              message: response.message,
            });
          }
        },
        error: (err) => {
          this.isLoading = false;
          this.snackbar.showCustom({
            type: 'error',
            title: this.validationMessages.TOASTER.ERROR.TITLE,
            message: this.validationMessages.TOASTER.ERROR.SOMETHING_WENT_WRONG,
          });
        },
      });
  }

  closeDeleteCommentDialog() {
    this.isDeleteCommentDialogOpen = false;
  }

  ngOnInit(): void {
    this.authService.getCurrentAccount().then((currentAccount) => {
      if (currentAccount) {
        this.name = currentAccount.name || '';
        const parts = this.name.split(/\s+/).filter(Boolean).slice(0, 2);
        this.name = parts.reverse().join(' ').replace(/,$/, '').trim();
        this.initials = parts.map((p) => p[0].toUpperCase()).join('');
      }
    });
    const storageUser = this.storageService.get('user');
    const user = JSON.parse(storageUser) as UserRolePermissionResponse;
    this.roles = user.roles ?? [];
    this.isWriter = this.roles?.includes('Writer');
    this.isReviewer = this.roles?.includes('Reviewer');
    

    this.currentUserId = user.userId;
    this.currentUserEmail = user.email;
    this.initialGenerate = history.state?.initialGenerate;
    this.route.queryParams.subscribe((params) => {
      this.reportId = params['reportId'];
    });
    this.getReportDetails();
    if (!this.initialGenerate) {
      this.loadSections(this.reportId, true);
    } else {
      this.reportId = history.state?.data;
      this.reportName = history.state?.reportData.reportName;
      this.loadSections(this.reportId, true);
    }
  }

  hasChild = (_: number, node: TocNode) => !!node.children?.length;

  private getCurrentStepperStatus(): number {
    return this.reportStatus ?? this.status ?? 0;
  }

  isStepDone(stepNumber: number): boolean {
    const status = this.getCurrentStepperStatus();

    if (status === 1) return stepNumber === 1;
    if (status === 2) return stepNumber <= 2;
    if (status === 3) return stepNumber <= 3;
    if (status === 4) return stepNumber <= 4;

    return false;
  }

  isStepActive(stepNumber: number): boolean {
    const status = this.getCurrentStepperStatus();

    if (status === 1) return stepNumber === 2;
    if (status === 2) return stepNumber === 3;
    if (status === 3) return stepNumber === 4;

    return false;
  }

  reportName: any;
  reportDetails: ReportV2Details | null = null;
  notes: any[] = [];
  newComment: string = '';

  loadSections(reportId: number, showLoader: boolean) {
    this.isLoading = showLoader;
    this.isReportStatusDialogOpen = false;
    this.reportV2Service.getSections(reportId).subscribe({
      next: (response) => {
        if (response.success) {
          const data = response.data;
          const reportStatusValue = data.status;

          // Status validation (previously in checkReportStatus)
          if (reportStatusValue === 7) {
            this.router.navigate(['/reports']);
            this.isLoading = false;
            return;
          } else if (reportStatusValue === 1) {
            this.reportStatusDialogButtonText = this.dialogTexts.REPORT_STATUS.CONFIRM;
            this.reportStatusDialogMessage = this.dialogTexts.REPORT_STATUS.MESSAGE_V2;
            this.isReportStatusDialogOpen = true;
            this.isLoading = false;
            this.reportStatus = reportStatusValue;
            return;
          } else if (reportStatusValue === 0 || reportStatusValue === 6) {
            this.isReportStatusDialogOpen = true;
            this.reportStatusDialogButtonText = '';
            this.reportStatusDialogMessage = this.dialogTexts.REPORT_STATUS.REGENERATE_MESSAGE;
            this.isLoading = false;
            this.reportStatus = reportStatusValue;
            return;
          }

          // Status is valid - bind sections data
          this.status = reportStatusValue;
          this.reportStatus = reportStatusValue;
          this.commentsCount = data.commentHistory ? data.commentHistory.length : 0;

          this.sectionNumberList = data.sectionList.map((section: any) => ({
            ...section,
            displayText: `${
              section.sectionNumber && section.sectionNumber !== '0.0'
                ? section.sectionNumber + ' '
                : ''
            }${section.sectionTitle || ''}`,
          }));

          this.reportName = data.reportName;
          this.clientName = data.clientName;
          this.locationName = data.siteName;
          this.createdBy = data.createdBy || '';
          this.statusLabel = ReportStatus[reportStatusValue] || '';
          this.overallQuality =
            data.qualityScore != null ? Number(data.qualityScore.toFixed(2)) : null;
          this.sectionsData = this.extractSections(data.sectionList);
          this.IsPublishEnabled =
            this.sectionsData.length > 0 &&
            this.sectionsData.every(
              (section) => section.status === SectionStatus[SectionStatus.Approved],
            );
          const initiatedStatus = SectionStatus[SectionStatus.Initiated];
          this.reworkSectionIds = this.sectionsData
            .filter((sec) => sec.status === initiatedStatus)
            .map((sec) => sec.id);
          this.startReworkPolling();

          this.dataSource.data = this.buildTocTree(data.sectionList);
          this.overAllQualityScore = data.averageQualityScore;

          // Initialize checked sections based on validationRequested flag
          this.checkedSections.clear();
          this.sectionsData.forEach((section) => {
            if (section.revisionRequested === 2) {
              this.checkedSections.add(section.id);
            }
          });

          // Bind section scores
          this.qualityScore = data.sectionList
            .filter((item: any) => item.parentSectionId === null)
            .map((item: any) => ({
              section:
                item.sectionNumber && item.sectionNumber !== '0.0'
                  ? `${item.sectionNumber} ${item.sectionTitle}`
                  : item.sectionTitle,
              score: Number(item.sectionScore),
              showBotProgress: item.showBotProgress,
            }));
          this.referenceFiles = data.reportReferences || [];
          this.getReworkSectionList();

          this.showScoreBoard = true;
        } else {
          this.snackbar.showCustom({
            type: 'error',
            title: this.validationMessages.TOASTER.ERROR.TITLE,
            message: response.message,
          });
        }
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        this.snackbar.showCustom({
          type: 'error',
          title: this.validationMessages.TOASTER.ERROR.TITLE,
          message: this.validationMessages.TOASTER.ERROR.SOMETHING_WENT_WRONG,
        });
      },
    });
  }

  handleReportConfirm(reportId: number) {
    this.loadSections(reportId, true);
  }

  redirectTohome() {
    this.router.navigate(['/reports']);
  }

  clearComment() {
    this.newComment = '';
  }

  openNotesPopup(sectionNumber: string, sectionId: number) {
    this.selectedSectionNumber = sectionNumber;
    this.selectedSectionIdForComments = sectionId;
    this.notesClosing = false;
    this.notesStatus = true;
    this.loadSectionComments(sectionId);
  }

  closeNotesPopup() {
    this.notesClosing = true;
    setTimeout(() => {
      this.notesStatus = false;
      this.notesClosing = false;
      this.notes = [];
    }, 250);
  }

  loadSectionComments(sectionId: number) {
    this.reportV2Service.getComments(this.reportId, sectionId).subscribe({
      next: (response) => {
        if (response.success) {
          this.notes = ((response.data as any[]) || []).map((note: any) => {
            const secNum = note.sectionNumber;
            const showSectionNumber =
              secNum !== null &&
              secNum !== undefined &&
              secNum !== '0' &&
              secNum !== '0.0' &&
              secNum !== 0;
            return {
              commentId: note.commentId,
              sectionId: note.sectionId,
              sectionNumber: showSectionNumber ? secNum : null,
              displayName: note.createdBy,
              createdByEmail: note.createdByEmail,
              userComment: note.commentText,
              updatedDate: this.parseUtcStringToDate(note.createdDate),
              isResolved: note.isResolved,
            };
          });
        }
      },
      error: (err) => {
        this.snackbar.showCustom({
          type: 'error',
          title: this.validationMessages.TOASTER.ERROR.TITLE,
          message: this.validationMessages.TOASTER.ERROR.SOMETHING_WENT_WRONG,
        });
      },
    });
  }

  postComment() {
    this.isLoading = true;
    if (!this.newComment?.trim()) {
      this.isLoading = false;
      return;
    }

    if (!Array.isArray(this.notes)) {
      this.notes = [];
    }

    const newNote = {
      id: this.notes.length + 1,
      initials: this.initials,
      name: this.name,
      section_number: this.selectedSectionNumber,
      timestamp: 'Just now',
      comment: this.newComment.trim(),
      bgColor: '#1C8768',
    };

    this.notes.push(newNote);

    this.saveComment();
  }

  saveComment() {
    if (!this.newComment?.trim()) {
      this.isLoading = false;
      return;
    }
    const request = {
      sectionId: this.selectedSectionIdForComments,
      sectionNumber: this.selectedSectionNumber || undefined,
      comment: this.newComment.trim(),
    };
    this.isLoading = true;
    this.reportV2Service.addComment(this.reportId, request).subscribe({
      next: (response) => {
        if (response.success) {
          this.newComment = '';
          this.commentsCount++;
          this.loadSectionComments(this.selectedSectionIdForComments);
        } else {
          this.snackbar.showCustom({
            type: 'error',
            title: this.validationMessages.TOASTER.ERROR.TITLE,
            message: response.message,
          });
        }
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
      },
    });
  }

  fetchSectionList(reportId: number, showLoader: boolean) {
    this.loadSections(reportId, showLoader);
  }

  startReworkPolling() {
    this.reworkInterval = setInterval(() => {
      if (this.reworkSectionIds.length === 0) {
        clearInterval(this.reworkInterval);
        this.reworkInterval = null;
        return;
      }
      this.getReworkSectionList();
    }, 15000);
  }

  getReworkSectionList() {
    if (!this.reworkSectionIds || this.reworkSectionIds.length === 0) {
      return;
    }
    this.reportV2Service.getReworkSections(this.reportId, this.reworkSectionIds).subscribe({
      next: (response) => {
        if (response.success) {
          const reworkSections = response.data || [];

          // Replace matched local sections with the latest mapped section data from v2 response.
          const mappedReworkSections = this.extractSections(reworkSections);
          mappedReworkSections.forEach((mappedSection) => {
            const index = this.sectionsData.findIndex((sec) => sec.id === mappedSection.id);
            if (index !== -1) {
              this.sectionsData[index] = mappedSection;
            }
          });

          //stop pooling
          const initiatedStatus = SectionStatus[SectionStatus.Initiated];
          this.reworkSectionIds = reworkSections
            .filter((sec) => sec.status === initiatedStatus)
            .map((sec) => sec.id);

          if (this.reworkSectionIds.length === 0 && this.reworkInterval) {
            clearInterval(this.reworkInterval);
            this.reworkInterval = null;
            this.checkedSections.clear();
          }
        }
      },
    });
  }

  openReferrenceDialog() {
    this.isReferenceDialogOpen = true;
  }

  closeReferrenceDialog() {
    this.isReferenceClosing = true;

    setTimeout(() => {
      this.isReferenceDialogOpen = false;
      this.isReferenceClosing = false;
    }, 300);
  }

  openApprovalDialog() {
    this.isApprovalDialogOpen = true;
    this.selectedReviewers = [];
    this.searchText = '';
  }
  closeApprovalDialog() {
    this.isApprovalDialogClosing = true;
    this.showSuggestions = false;
    setTimeout(() => {
      this.isApprovalDialogOpen = false;
      this.isApprovalDialogClosing = false;
      this.selectedReviewers = [];
      this.searchText = '';
    }, 300);
  }

  parseUtcStringToDate(value: string | null | undefined): Date | null {
    if (!value) return null;
    const hasTz = /[zZ]|[+\-]\d{2}:\d{2}$/.test(value);
    const normalized = hasTz ? value : value + 'Z';
    const d = new Date(normalized);
    return isNaN(d.getTime()) ? null : d;
  }

  editSection(section: Section) {
    section.originalState = { name: section.name, content: section.content };
    section.isEditing = true;
  }

  saveSection(section: Section) {
    if (!section.id) {
      this.snackbar.showCustom({
        type: 'error',
        title: this.validationMessages.TOASTER.ERROR.TITLE,
        message: this.validationMessages.REPORT_SECTION.SECTION_NUMBER_REQUIRED,
      });
      return;
    }
    if (!section.name || section.name.trim() === '') {
      this.snackbar.showCustom({
        type: 'error',
        title: this.validationMessages.TOASTER.ERROR.TITLE,
        message: this.validationMessages.REPORT_SECTION.SECTION_NUMBER_REQUIRED,
      });
      return;
    }

    this.isLoading = true;
    section.isEditing = false;
    delete section.originalState;

    this.reportV2Service
      .updateSectionContent(this.reportId, section.id, section.content)
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.isLoading = false;
            this.snackbar.showCustom({
              type: 'success',
              title: this.validationMessages.TOASTER.SUCCESS.TITLE,
              message: this.apiResponseMessages.SECTION_UPDATED,
            });
            this.fetchSectionList(this.reportId, true);
          } else {
            this.isLoading = false;
            this.snackbar.showCustom({
              type: 'error',
              title: this.validationMessages.TOASTER.ERROR.TITLE,
              message: this.apiResponseMessages.FAILED_TO_UPDATE_SECTION,
            });
          }
        },
        error: (err) => {
          this.isLoading = false;
          this.snackbar.showCustom({
            type: 'error',
            title: this.validationMessages.TOASTER.ERROR.TITLE,
            message: this.validationMessages.TOASTER.ERROR.SOMETHING_WENT_WRONG,
          });
        },
      });
  }

  cancelEdit(section: Section) {
    if (section.originalState) {
      section.name = section.originalState.name;
      section.content = section.originalState.content;
    }
    section.isEditing = false;
    delete section.originalState;
  }

  checkSectionSelctionStatus() {
    const selectedSectionIds = this.sectionsData
      .filter((section) => this.isSectionChecked(section.id))
      .map((section) => section.id);
    if (selectedSectionIds.length === 0) {
      this.snackbar.showCustom({
        type: 'error',
        title: this.validationMessages.TOASTER.ERROR.TITLE,
        message: this.validationMessages.TOASTER.ERROR.NO_SECTION_SELECTED,
      });
      return;
    }
    this.sendToAgentForRework(selectedSectionIds);
  }

  sendToAgentForRework(selectedParentIds: number[] = []) {
    this.isLoading = true;
    this.reportV2Service.triggerRework(this.reportId, selectedParentIds).subscribe({
      next: (response) => {
        if (response.success) {
          this.snackbar.showCustom({
            type: 'success',
            title: this.validationMessages.TOASTER.SUCCESS.TITLE,
            message: this.apiResponseMessages.SECTION_SENT_REWORK,
          });
          this.fetchSectionList(this.reportId, true);
        } else {
          this.isLoading = false;
          this.snackbar.showCustom({
            type: 'error',
            title: this.validationMessages.TOASTER.ERROR.TITLE,
            message: response.message,
          });
        }
      },
      error: (err) => {
        this.isLoading = false;
        const apiMessage = err?.error?.message;
        this.snackbar.showCustom({
          type: 'error',
          title: this.validationMessages.TOASTER.ERROR.TITLE,
          message: apiMessage || this.validationMessages.TOASTER.ERROR.SOMETHING_WENT_WRONG,
        });
      },
    });
  }

  getUserColor(userName: string): string {
    const colors = ['#E52713', '#1C8768', '#FFC107', '#008768', '#AECC53', '#00353E'];

    if (!userName) return colors[0];

    let hash = 0;
    for (let i = 0; i < userName.length; i++) {
      hash = userName.charCodeAt(i) + ((hash << 5) - hash);
    }

    const index = Math.abs(hash) % colors.length;
    return colors[index];
  }

  scrollTo(sectionId: string) {
    const element = document.getElementById('sectionContainer-' + sectionId);
    if (!element) return;

    const container =
      (element.closest('.section-scroll') as HTMLElement | null) ||
      (document.scrollingElement as HTMLElement);
    const header = container?.querySelector('.hearder-part') as HTMLElement | null;
    const headerHeight = header ? header.offsetHeight : 0;

    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const offset = Math.max(
      0,
      elementRect.top - containerRect.top + container.scrollTop - headerHeight - 8,
    );

    container.scrollTo({ top: offset, behavior: 'smooth' });

    if (this.isTOCOverlayOpen) {
      this.closeTOCOverlay();
    }
  }

  private extractSections(sections: SectionV2[]): Section[] {
    const sortedSections = sections.sort((a, b) => a.sectionSequence - b.sectionSequence);
    return sortedSections.map((sec) => ({
      id: sec.id,
      sectionNumber:
        sec.sectionNumber === '0.0' || sec.sectionNumber == null ? '' : sec.sectionNumber,
      name: sec.sectionTitle || '',
      content: sec.sectionContent || '',
      isEditing: false,
      revisionRequested: sec.revisionRequested,
      showBotProgress: sec.showBotProgress ?? false,
      reviewerIds: sec.reviewerIds || null,
      isEditable: sec.isEditable ?? true,
      sectionScore: sec.sectionScore ?? null,
      status: sec.status || undefined,
    }));
  }

  private buildTocTree(sections: any[]): TocNode[] {
    const sectionMap = new Map<number, TocNode>();
    sections.forEach((section) => {
      sectionMap.set(section.id, {
        id: section.id,
        name:
          section.sectionNumber && section.sectionNumber !== '0.0'
            ? `${section.sectionNumber} ${section.sectionTitle}`
            : `${section.sectionTitle}`,
        children: [],
      });
    });

    const tocRoots: TocNode[] = [];
    sections.forEach((section) => {
      const currentNode = sectionMap.get(section.id);
      if (!currentNode) return;

      if (section.parentSectionId) {
        const parentNode = sectionMap.get(section.parentSectionId);
        if (parentNode && parentNode.children) {
          parentNode.children.push(currentNode);
        }
      } else {
        tocRoots.push(currentNode);
      }
    });

    const sortBySequence = (nodes: TocNode[]) => {
      nodes.sort((a, b) => {
        const seqA = sections.find((s) => s.id === a.id)?.sectionSequence ?? 0;
        const seqB = sections.find((s) => s.id === b.id)?.sectionSequence ?? 0;
        return seqA - seqB;
      });
      nodes.forEach((n) => sortBySequence(n.children || []));
    };

    sortBySequence(tocRoots);
    return tocRoots;
  }

  isSectionChecked(sectionId: number): boolean {
    return this.checkedSections.has(sectionId);
  }

  onSectionCheckboxChange(section: Section, checked: boolean): void {
    if (checked) {
      this.checkedSections.add(section.id);
    } else {
      this.checkedSections.delete(section.id);
    }
  }

  getInitials(name: string): string {
    if (!name) return '';
    const parts = name.trim().split(' ');
    if (parts.length === 1) {
      return parts[0][0].toUpperCase();
    }
    return (parts[0][0] || '').toUpperCase() + (parts[parts.length - 1][0] || '').toUpperCase();
  }

  fetchReviewers(clientId: number) {
    this.reportV2Service.getReviewers(clientId).subscribe({
      next: (response) => {
        if (response.success) {
          this.reviewerList = response.data;
        }
      },
      error: (err) => {
        this.snackbar.showCustom({
          type: 'error',
          title: this.validationMessages.TOASTER.ERROR.TITLE,
          message: this.validationMessages.TOASTER.ERROR.SOMETHING_WENT_WRONG,
        });
      },
    });
  }

  searchText: string = '';
  filteredReviewers: ReviewerV2[] = [];
  showSuggestions = false;
  selectedReviewers: ReviewerV2[] = [];

  onSearch() {
    const search = this.searchText.toLowerCase();

    this.filteredReviewers = this.reviewerList.filter(
      (r) =>
        (r.displayName?.toLowerCase().includes(search) ||
          r.email?.toLowerCase().includes(search)) &&
        !this.selectedReviewers.some((s) => s.reviewerId === r.reviewerId),
    );
  }

  selectReviewer(reviewer: ReviewerV2) {
    if (!this.selectedReviewers.some((r) => r.reviewerId === reviewer.reviewerId)) {
      this.selectedReviewers.push(reviewer);
    }
    this.searchText = '';
    this.showSuggestions = false;
  }

  removeReviewer(reviewer: ReviewerV2) {
    this.selectedReviewers = this.selectedReviewers.filter(
      (r) => r.reviewerId !== reviewer.reviewerId,
    );
  }

  clearSelectedReviewers() {
    this.selectedReviewers = [];
    this.searchText = '';
    this.showSuggestions = false;
  }

  onSearchBlur() {
    // Add a small delay to allow click event on suggestion items to fire
    setTimeout(() => {
      this.showSuggestions = false;
    }, 150);
  }

  addReviewerForApproval() {
    if (this.selectedReviewers.length === 0) {
      this.snackbar.showCustom({
        type: 'error',
        title: this.validationMessages.TOASTER.ERROR.TITLE,
        message: this.validationMessages.REPORT_SECTION.SELECT_REVIEWER,
      });
      return;
    }
    if (!this.selectedSection) {
      this.snackbar.showCustom({
        type: 'error',
        title: this.validationMessages.TOASTER.ERROR.TITLE,
        message: this.validationMessages.TOASTER.ERROR.SOMETHING_WENT_WRONG,
      });
      return;
    }
    this.isLoading = true;
    const reviewerIds = this.selectedReviewers.map((r) => r.reviewerId);
    this.isApprovalDialogOpen = false;
    this.reportV2Service
      .submitForReview(this.reportId, this.selectedSection.id, reviewerIds)
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.snackbar.showCustom({
              type: 'success',
              title: this.validationMessages.TOASTER.SUCCESS.TITLE,
              message: this.apiResponseMessages.SENT_FOR_APPROVAL,
            });
            this.selectedReviewers = [];
            this.selectedSection = null;
            this.loadSections(this.reportId, true);
          } else {
            this.isLoading = false;
            this.snackbar.showCustom({
              type: 'error',
              title: this.validationMessages.TOASTER.ERROR.TITLE,
              message: response.message,
            });
          }
        },
        error: (err) => {
          this.isLoading = false;
          const errorMessage =
            err?.error?.message || this.validationMessages.TOASTER.ERROR.SOMETHING_WENT_WRONG;
          this.snackbar.showCustom({
            type: 'error',
            title: this.validationMessages.TOASTER.ERROR.TITLE,
            message: errorMessage,
          });
        },
      });
  }

  // Direct TOC overlay (not using MatDialog)
  isTOCOverlayOpen = false;

  openTOC() {
    this.isTOCOverlayOpen = true;
    document.body.classList.add('overlay-dialog-open');
  }

  closeTOCOverlay() {
    this.isTOCOverlayOpen = false;
    document.body.classList.remove('overlay-dialog-open');
  }

  isScoresDialogOpen = false;
  openScores() {
    this.isScoresDialogOpen = true;
    document.body.classList.add('overlay-dialog-open');
  }

  closeScores() {
    this.isScoresDialogOpen = false;
    document.body.classList.remove('overlay-dialog-open');
  }

  selectSection(section: Section) {
    this.selectedSection = section;
  }

  getReviewerNames(reviewerIds: string[] | null): string[] {
    if (!reviewerIds || reviewerIds.length === 0) {
      return [];
    }

    return reviewerIds
      .map((reviewerId) =>
        this.reviewerList.find((reviewer) => reviewer.reviewerId === reviewerId)?.displayName,
      )
      .filter((name): name is string => !!name);
  }

  getReviewerCount(reviewerIds: string[] | null): number {
    return this.getReviewerNames(reviewerIds).length;
  }

  hasMappedReviewers(reviewerIds: string[] | null): boolean {
    return this.getReviewerCount(reviewerIds) > 0;
  }

  deselectSection() {
    this.selectedSection = null;
  }

  isCurrentUserReviewer(section: Section): boolean {
    if (!this.currentUserId || !section.reviewerIds) return false;
    return section.reviewerIds.includes(this.currentUserId);
  }

  approveSection() {
    if (!this.selectedSection || !this.currentUserId) return;
    this.feedbackDialogAction = 'approve';
    this.confirmApproveSection();
  }

  closeApproveSectionDialog() {
    this.isApproveSectionDialogClosing = true;
    setTimeout(() => {
      this.isApproveSectionDialogOpen = false;
      this.isApproveSectionDialogClosing = false;
      this.approveFeedback = '';
    }, 300);
  }

  confirmApproveSection() {
    if (!this.selectedSection || !this.currentUserId) return;
    this.isLoading = true;
    const request: any = {
      reviewerId: this.currentUserId,
      feedback: null,
      attemptNumber: 1,
    };
    this.reportV2Service.approveSection(this.reportId, this.selectedSection.id, request).subscribe({
      next: (response) => {
        if (response.success) {
          this.snackbar.showCustom({
            type: 'success',
            title: this.validationMessages.TOASTER.SUCCESS.TITLE,
            message: response.message,
          });
          this.approveFeedback = '';
          this.loadSections(this.reportId, false);
        } else {
          this.snackbar.showCustom({
            type: 'error',
            title: this.validationMessages.TOASTER.ERROR.TITLE,
            message: response.message || this.validationMessages.TOASTER.ERROR.SOMETHING_WENT_WRONG,
          });
        }
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        const errorMessage =
          err?.error?.message || this.validationMessages.TOASTER.ERROR.SOMETHING_WENT_WRONG;
        this.snackbar.showCustom({
          type: 'error',
          title: this.validationMessages.TOASTER.ERROR.TITLE,
          message: errorMessage,
        });
      },
    });
  }

  rejectSection() {
    if (!this.selectedSection || !this.currentUserId) return;
    this.feedbackDialogAction = 'reject';
    this.confirmRejectSection();
  }

  confirmApproveOrRejectSection() {
    if (!this.selectedSection || !this.currentUserId) return;
    if (this.feedbackDialogAction === 'approve') {
      this.confirmApproveSection();
    } else {
      this.confirmRejectSection();
    }
  }

  confirmRejectSection() {
    if (!this.selectedSection || !this.currentUserId) return;
    this.isLoading = true;
    const request: any = {
      reviewerId: this.currentUserId,
      feedback: null,
      attemptNumber: 1,
    };
    this.reportV2Service.rejectSection(this.reportId, this.selectedSection.id, request).subscribe({
      next: (response) => {
        if (response.success) {
          this.snackbar.showCustom({
            type: 'success',
            title: this.validationMessages.TOASTER.SUCCESS.TITLE,
            message: response.message,
          });
          this.approveFeedback = '';
          this.loadSections(this.reportId, false);
        } else {
          this.snackbar.showCustom({
            type: 'error',
            title: this.validationMessages.TOASTER.ERROR.TITLE,
            message: response.message || this.validationMessages.TOASTER.ERROR.SOMETHING_WENT_WRONG,
          });
        }
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        const errorMessage =
          err?.error?.message || this.validationMessages.TOASTER.ERROR.SOMETHING_WENT_WRONG;
        this.snackbar.showCustom({
          type: 'error',
          title: this.validationMessages.TOASTER.ERROR.TITLE,
          message: errorMessage,
        });
      },
    });
  }

  publishReport() {
    this.isLoading = true;
    this.reportV2Service.publishReport(this.reportId).subscribe({
      next: (response) => {
        if (response.success) {
          this.snackbar.showCustom({
            type: 'success',
            title: this.validationMessages.TOASTER.SUCCESS.TITLE,
            message: response.message,
          });
          this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
            this.router.navigate(['/reports']);
          });
        } else {
          this.snackbar.showCustom({
            type: 'error',
            title: this.validationMessages.TOASTER.ERROR.TITLE,
            message: response.message || this.validationMessages.TOASTER.ERROR.SOMETHING_WENT_WRONG,
          });
        }
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        const errorMessage =
          err?.error?.message || this.validationMessages.TOASTER.ERROR.SOMETHING_WENT_WRONG;
        this.snackbar.showCustom({
          type: 'error',
          title: this.validationMessages.TOASTER.ERROR.TITLE,
          message: errorMessage,
        });
      },
    });
  }

  getReportDetails() {
    this.isLoading = true;
    this.reportV2Service.getReportDetails(this.reportId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.reportDetails = response.data;
          this.reviewerList = [];
          this.fetchReviewers(response.data.clientId);
        } else {
          this.snackbar.showCustom({
            type: 'error',
            title: this.validationMessages.TOASTER.ERROR.TITLE,
            message: response.message || this.validationMessages.TOASTER.ERROR.SOMETHING_WENT_WRONG,
          });
        }
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        const errorMessage =
          err?.error?.message || this.validationMessages.TOASTER.ERROR.SOMETHING_WENT_WRONG;
        this.snackbar.showCustom({
          type: 'error',
          title: this.validationMessages.TOASTER.ERROR.TITLE,
          message: errorMessage,
        });
      },
    });
  }
}
